import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { quartzActions } from '../../../actions/quartzActions'
import userApi from '../../../api/UserApi'
import QuartzJobInner, { isCronValid } from './QuartzJobs'
import { Loader } from '../../common/loader/Loader'
import * as commonSelectors from '../../common/selectors'
import * as quartzSelectors from './selectors'
import { commonActions } from '../../../actions/commonActions'
import GlobalModal from '../../common/GlobalModal'
import Card from '../../common/core/Card'
import { Toolbar, ToolbarButton } from '../../common/core/Toolbar'
import { FaRetweet, FaSave, FaTimes } from 'react-icons/fa'
import ModalEnum from '../../../shared/enums/modalEnum'
import { isNullOrEmpty } from '../../../shared/scripts'
import './quartzEditor.css'
import '../../../styles/Configuration.css'


const QuartzEditor = () => {
    const [saveConfirm, setSaveConfirm] = useState(false);
    const [discardConfirm, setDiscardConfirm] = useState(false);
    const [hasRight, setHasRight] = useState(false);
    const [isReadOnly, setReadOnly] = useState(true);

    const isAuthorized = useSelector(commonSelectors.isAuthorized);
    const rights = useSelector(commonSelectors.userRights);

    const environments = useSelector(quartzSelectors.environments);
    const loaded = useSelector(quartzSelectors.loaded);
    const loadingSelector = useSelector(quartzSelectors.loading);
    const changes = useSelector(quartzSelectors.changes);
    const activeKey = useSelector(quartzSelectors.activeKey);
    const serverValues = useSelector(quartzSelectors.serverValues);
    const infoMessageSelector = useSelector(quartzSelectors.infoMessage);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        const _hasRight = rights?.fullControl ?? false;
        if (_hasRight !== hasRight) {
            setHasRight(_hasRight);
            setReadOnly(!_hasRight);
        }

        if (isAuthorized === false) {
            userApi.authorize();
        } else if (isAuthorized === true && !_hasRight) {
            commonActions.history.accessForbidden();
        }
        if (!loaded && _hasRight) {
            dispatch(
                quartzActions.initializeQuartzEditor()
            );
        }
    }, [isAuthorized, rights, hasRight, isReadOnly, loaded, navigate, dispatch]);

    const handleReloadData = useCallback(() => {
        dispatch(quartzActions.setLoading(true));
        dispatch(quartzActions.initializeQuartzEditor());
    }, [dispatch]);

    const handleSave = useCallback(() => {
        setSaveConfirm(false)
        dispatch(
            quartzActions.setLoading(true)
        );
        const result = {
            jobChanges: [],
            triggerChanges: [],
        };

        for (let i = 0; i < changes.triggerChanges.length; i++) {
            const change = changes.triggerChanges[i];

            const env = change.type === 1
                ? serverValues.find(x => x.environmentName === change.env)
                : environments.find(x => x.environmentName === change.env);
            const job = env.jobs.find(x => x.jobName === change.job)
            const trigger = job.triggers.find(x => x.id === change.id);

            if (change.type !== 0) {
                const oldenv = serverValues.find(x => x.environmentName === change.env);

                const oldjob = oldenv.jobs.find(x => x.jobName === change.job);
                const oldtrigger = oldjob.triggers.find(x => x.id === change.id);
                const oldCron = oldtrigger.cron;
                const oldTenant = oldtrigger.tenant;
                const oldJobType = oldtrigger.jobType;

                result
                    .triggerChanges
                    .push({
                        id: trigger.id,
                        changeType: change.type,
                        jobName: trigger.jobName,
                        cron: trigger.cron,
                        tenant: trigger.tenant,
                        env: env.environmentName,
                        oldCron: oldCron,
                        oldTenant: oldTenant,
                        jobType: job.jobType,
                        oldJobType: oldJobType
                    });
            } else {
                result
                    .triggerChanges
                    .push({
                        id: trigger.id,
                        changeType: change.type,
                        jobName: trigger.jobName,
                        cron: trigger.cron,
                        tenant: trigger.tenant,
                        env: env.environmentName,
                        oldCron: trigger.cron,
                        oldTenant: trigger.tenant,
                        jobType: job.jobType,
                        oldJobType: job.jobType
                    });
            }
        }

        for (let i = 0; i < changes.jobChanges.length; i++) {
            const change = changes.jobChanges[i];
            const env = environments[0];
            const job = env.jobs.find(x => x.jobName === change.jobName);
            result
                .jobChanges
                .push({
                    jobType: job.jobType,
                    jobName: job.jobName,
                });
        }

        let triggerValidation = "";

        for (let i = 0; i < environments.length; i++) {
            const env = environments[i];
            for (let j = 0; j < env.jobs.length; j++) {
                const job = env.jobs[j];
                const jobTriggers = job.triggers;
                for (let x = 0; x < job.triggers.length; x++) {
                    let trigger = job.triggers[x];
                    let validCron = isCronValid(trigger.cron);
                    if ((isNullOrEmpty(trigger.tenant) && trigger.jobType !== "SystemJob") || !validCron) {
                        triggerValidation = triggerValidation + "<p><strong>InvalidTrigger</strong>-" + trigger.environment + "-" + trigger.jobName + "-";
                        triggerValidation = triggerValidation + '<strong style="color: ' + (isNullOrEmpty(trigger.tenant) ? 'red' : 'black') + '">';
                        triggerValidation = triggerValidation + (isNullOrEmpty(trigger.tenant) ? "NULL" : trigger.tenant) + '</strong>-';
                        triggerValidation = triggerValidation + '<strong style="color: ' + (validCron === false ? 'red' : 'black') + '">';
                        triggerValidation = triggerValidation + (isNullOrEmpty(trigger.cron) ? "NULL" : trigger.cron);
                        triggerValidation = triggerValidation + '</strong></p>';
                    }
                    const jobTriggersCount = jobTriggers.length;
                    const jobTriggersOtherTenantCount = jobTriggers.reduce((acc, x) => x.tenant !== trigger.tenant ? ++acc : acc, 0);
                    if ((jobTriggersCount - jobTriggersOtherTenantCount) > 1) {
                        triggerValidation = triggerValidation + "<p><strong style='color: red;'>DuplicitTrigger</strong>-" + trigger.Environment + "-" + trigger.JobName + "-";
                        triggerValidation = triggerValidation + '<strong style="color: ' + (isNullOrEmpty(trigger.tenant) ? 'red' : 'black') + '">';
                        triggerValidation = triggerValidation + (isNullOrEmpty(trigger.tenant) ? "NULL" : trigger.Tenant) + '</strong>';
                        triggerValidation = triggerValidation + '</p>';
                    }
                }
            }
        }
        if (triggerValidation !== "") {
            dispatch(quartzActions.setMessage(triggerValidation));
            dispatch(quartzActions.setLoading(false));
        } else {
            dispatch(
                quartzActions.save({
                    triggerChanges: result.triggerChanges,
                    jobChanges: result.jobChanges
                })
            )
        }
    }, [changes, dispatch, environments, serverValues]);

    const handleDiscard = useCallback(() => {
        setDiscardConfirm(false);
        dispatch(quartzActions.discardAllChanges())
    }, [dispatch]);

    const handleCloseInfo = useCallback(() => {
        dispatch(
            quartzActions.setMessage("")
        );
    }, [dispatch]);

    const handleChangeActiveKey = useCallback((_event, key) => {
        dispatch(quartzActions.setActiveKey(key));
    }, [dispatch]);


    return (
        <div>
            {hasRight && environments != null && loaded &&
                <Card
                    className='quartz-editor-card'
                    header={<>
                        <span className='title'>
                            <span>Quartz Editor</span>
                        </span>
                        <span className='counts'>
                            <span title='added triggers count'>
                                <span>added: </span>
                                <strong
                                    className={changes.addedTriggers > 0 ? 'green' : ''}
                                >
                                    {changes.addedTriggers}
                                </strong>
                            </span>
                            <span title='edited triggers count'>
                                <span>edited: </span>
                                <strong
                                    className={changes.editedTriggers > 0 ? 'green' : ''}
                                >
                                    {changes.editedTriggers}
                                </strong>
                            </span>
                            <span title='removed triggers count'>
                                <span>removed: </span>
                                <strong
                                    className={changes.deletedTriggers > 0 ? 'green' : ''}
                                >
                                    {changes.deletedTriggers}
                                </strong>
                            </span>
                        </span>
                    </>}
                    padding={false}
                    toolbar={
                        <Toolbar>
                            <ToolbarButton
                                onClick={() => handleReloadData()}
                                text="Refresh"
                                icon={<FaRetweet />}
                            />
                            {(changes.jobChanges.length > 0 || changes.triggerChanges.length > 0) &&
                                <>
                                    <ToolbarButton
                                        onClick={() => setSaveConfirm(true)}
                                        text="Save"
                                        icon={<FaSave />}
                                        color="green"
                                    />
                                    <ToolbarButton
                                        onClick={() => setDiscardConfirm(true)}
                                        text="Discard"
                                        icon={<FaTimes />}
                                        color="red"
                                    />
                                </>
                            }
                        </Toolbar>
                    }
                >
                    <TabsContainer
                        activeKey={activeKey}
                        environments={environments}
                        isReadOnly={isReadOnly}
                        onChange={index => handleChangeActiveKey(null, index)}
                    />
                    <GlobalModal
                        type={ModalEnum.Confirmation}
                        isOpen={saveConfirm}
                        accept={() => handleSave()}
                        decline={() => setSaveConfirm(false)}
                        content={{ text: "Are you sure to save all changes?" }}
                    />
                    <GlobalModal
                        type={ModalEnum.Confirmation}
                        isOpen={discardConfirm}
                        accept={() => handleDiscard()}
                        decline={() => setDiscardConfirm(false)}
                        content={{ text: "Are you sure to discard all changes?" }}
                    />
                    <GlobalModal
                        type={ModalEnum.Information}
                        isOpen={infoMessageSelector != null && infoMessageSelector.length > 0}
                        close={() => handleCloseInfo()}
                        content={{ 
                            html: true,
                            text: infoMessageSelector,
                         }}
                    />
                </Card>
            }
            <Loader
                isloading={!loaded || loadingSelector || isAuthorized !== true}
            />
        </div>
    );
}

const TabsContainer = ({
    activeKey,
    environments = [],
    isReadOnly = true,
    onChange = null,
}) => {

    const handleClick = index => {
        if (onChange != null) {
            onChange(index);
        }
    };

    return (
        <div>
            <nav className='pills-wrapper'>
                <div
                    className="nav nav-pills"
                    id="pills-tab"
                    role="tablist"
                >
                    {environments.length > 0 && environments.map((env, index) => {
                        const { environmentName } = env
                        return (
                            <button
                                key={index}
                                className={`nav-link ${index === activeKey ? 'active' : ''}`}
                                id={`pill-${index}-tab`}
                                data-bs-toggle="pill"
                                data-bs-target={`#pill-${index}`}
                                type="button"
                                role="tab"
                                aria-controls={`pill-${environmentName}`}
                                aria-selected={index === activeKey}
                                onClick={() => handleClick(index)}
                            >
                                {
                                    environmentName
                                }
                            </button>
                        )
                    })}
                </div>
            </nav>
            <div
                className="tab-content"
                id="pills-tabContent"
            >
                {environments.map((env, index) => {
                    const { environmentName } = env
                    return (
                        <div
                            id={`pill-${index}`}
                            className={`tab-pane fade show ${index === activeKey ? 'active' : ''}`}
                            key={index}
                            role="tabpanel"
                            aria-labelledby={`pill-${environmentName}-tab`}
                        >
                            <Jobs
                                environmentName={environmentName}
                                jobs={env.jobs}
                                isReadOnly={isReadOnly}
                            />
                        </div>
                    )
                })}
            </div>
        </div>
    );
}


const Jobs = ({
    environmentName,
    jobs = [],
    isReadOnly = true,
}) => {
    return (
        <div>
            {jobs.map((job, index) => {
                return (
                    <QuartzJobInner
                        key={`${job.class}-${job.jobType}`}
                        index={index}
                        envName={environmentName}
                        jobName={job.jobName}
                        readOnly={isReadOnly}
                    />
                )
            })}
        </div>
    );
}


export default QuartzEditor;